/**
 * One table, open on the Dashboard: its controls and the report itself. The
 * page owns the data and the view; this is the switchboard.
 *
 * There used to be a page of these behind tabs, with "+ more" to bring back a
 * closed tab. The Dashboard and All views do both jobs now.
 */
import type { ReactNode } from 'react';
import api from '../../../services/api';
import UpdatingNote from './UpdatingNote';
import ReconRunsReport from './ReconRunsReport';
import FeatureCloudReport from './FeatureCloudReport';
import type { BoardroomFrameAxis, BoardroomReport, BoardroomReportKey, BoardroomRunFindings } from '../../../types';
import type { View } from '../filters';
import type { OnDig } from './MissingList';
import DemandReport from './DemandReport';
import CompetitorsReport from './CompetitorsReport';
import FeatureMatrixReport from './FeatureMatrixReport';
import LandscapeReport from './LandscapeReport';
import WhoRanksReport from './WhoRanksReport';
import SearchMarketReport from './SearchMarketReport';
import BrandGridReport from './BrandGridReport';
import EmailCaptureReport from './EmailCaptureReport';
import ContactsReport from './ContactsReport';
import AppealReport from './AppealReport';
import RelatedReport from './RelatedReport';
import OpportunityBriefReport from './OpportunityBriefReport';
import PainGapReport from './PainGapReport';
import AudienceReport from './AudienceReport';
import HunchesReport from './HunchesReport';
import PriceMapReport from './PriceMapReport';
import StoryReport from './StoryReport';
import PeopleReport from './PeopleReport';
import AnglesReport from './AnglesReport';
import ReportControls from './ReportControls';
import type { ColumnChange } from './columnChange';

/** An axis as a person reads it: SOLUTION_TYPE → solution type. */
const nice = (d: string) => d.toLowerCase().replace(/_/g, ' ');

/** One axis off the frame. Both ends of the cross use it, because both ends
 *  are the same act — which is the thing the old pair of controls hid. */
function AxisPick({
  label,
  title,
  value,
  options,
  onPick,
  none,
}: {
  label: string;
  title: string;
  value: string;
  options: string[];
  onPick: (dimension: string) => void;
  /** The empty choice, for the end that may have none. Omitted, the axis is
   *  required — a landscape with no rows is not a view of anything. */
  none?: string;
}) {
  return (
    <select
      aria-label={label}
      title={title}
      value={value}
      onChange={e => onPick(e.target.value)}
      className="rounded border border-gray-300 px-1 py-0.5 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
    >
      {none && <option value="">{none}</option>}
      {options.map(d => (
        <option key={d} value={d}>
          {nice(d)}
        </option>
      ))}
    </select>
  );
}

export default function Reports({
  report,
  view,
  onView,
  data,
  loading,
  onOpen,
  onDig,
  digging,
  unseen,
  onSetRelevance,
  sortingKinds,
  onSeen,
  extra,
  onCrossBy,
  onPickTag,
  roomName,
  columnChange,
  onUndoColumns,
  onSplitType,
  onUndoSplit,
  splittingType,
  boardroomId,
  canPublish,
  onOpenTable,
  lastDig,
  summary,
  loadFindings,
  focusRun,
}: {
  report: BoardroomReportKey;
  view: View;
  onView: (next: View) => void;
  data: BoardroomReport | null;
  loading: boolean;
  onOpen: (itemId: number) => void;
  onDig: OnDig;
  digging: boolean;
  /** The room's dig counter, threaded to every table so a row changed by a
   *  recent dig is marked and fades over the following few. */
  unseen?: ReadonlySet<number>;
  /** Say whether a KIND of thing is what this room is about. One answer moves
   *  everything filed under it, on and off every table. */
  onSetRelevance?: (itemId: number, relevance: 'core' | 'related') => void;
  sortingKinds?: boolean;
  onSeen?: (ids: number[]) => void;
  /** Names the CSV a reader downloads, so a saved table says which room. */
  roomName?: string;
  /** What the room last did to its columns, and the way back. */
  columnChange?: ColumnChange | null;
  onUndoColumns?: (frameId: number) => Promise<void> | void;
  /** Split a type that is doing the work of several markets. */
  onSplitType?: (typeId: number) => void;
  /** Put the last split back. */
  onUndoSplit?: (typeId: number) => void;
  splittingType?: boolean;
  /** The room, so the opportunity brief can offer the way out of it. */
  boardroomId?: number;
  /** Whether this account holds Publish, which is what a subject belongs to. */
  canPublish?: boolean;
  extra?: ReactNode;
  /** The one axis the landscape crosses every section by. */
  /** A dimension, or '' for plain lists. */
  onCrossBy?: (dimension: string) => void;
  onPickTag?: (key: string) => void;
  /** Open one of the room's tables - a Recon Runs row aimed at one. */
  onOpenTable?: (key: BoardroomReportKey) => void;
  /** The last dig aimed at this table, at the top of it (`LastDigSummary`). */
  lastDig?: ReactNode;
  /** "What this means" for the room's question, at the very top (`TableSummary`). */
  summary?: ReactNode;
  /** Recon Runs: what one dig found, and a dig to open on arrival. */
  loadFindings?: (runId: number) => Promise<BoardroomRunFindings>;
  focusRun?: { runId: number; at: number } | null;
}) {
  return (
    <div className="flex flex-col gap-2 min-h-0 flex-1">
      <div className="flex flex-wrap items-center gap-3">
        {/* The text filter is the room's, in the top bar beside Find: it
            narrows every table and the board's cards, wherever you go. */}
        <ReportControls view={view} onView={onView} extra={extra} />
        {/* Only over a table already drawn; an empty one says so below. */}
        {data && <UpdatingNote loading={loading} digging={digging} />}
      </div>
      {report === 'landscape' && data?.kind === 'landscape' && data.axes.length > 0 && (
        /* ONE CHOICE, BECAUSE THERE IS ONLY ONE. The landscape draws a section
           per axis, each with its own axis down the side — so which axis is
           the rows is which table you are reading, and the picker that used to
           choose it could not move a single row. It survived the change that
           stacked every axis and went on offering the choice anyway, wearing
           chips that looked like the tag filters in the rail: a control that
           CHOOSES the view, read as one that NARROWS it, doing neither.

           What is left is real and applies to every table on the page. */
        <div
          className="flex flex-wrap items-center gap-1.5 text-xs"
          data-testid="axis-buttons"
        >
          <span className="text-[11px] uppercase tracking-wide text-gray-400">crossed by</span>
          <select
            aria-label="Column axis"
            title="Every table on this page runs its own tag group down the side and this one across the top. Each cell is the players carrying both values, and an empty cell is a combination nobody covers - which is the point of the view."
            value={data.b ?? ''}
            // '' is a choice (plain lists), not "no choice": null would let the
            // server pick the most-used axis and the select would jump back.
            onChange={e => onCrossBy?.(e.target.value)}
            className="rounded border border-gray-300 px-1 py-0.5 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="">nothing · plain lists</option>
            {data.axes.map(d => (
              <option key={d} value={d}>
                {nice(d)}
              </option>
            ))}
          </select>
          <span className="ml-auto text-xs text-gray-400">
            {data.totals.placed} of {data.totals.players} players placed
            {data.b && ` on ${nice(data.b)}`}
          </span>
        </div>
      )}

      {summary}
      {lastDig}
      {data && data.kind === report ? (
        data.kind === 'feature_cloud' ? (
          <FeatureCloudReport
            data={data}
            onOpen={onOpen}
            twins={
              boardroomId
                ? {
                    load: () => api.getBoardroomFeatureTwins(boardroomId),
                    merge: groups => api.mergeBoardroomGroups(boardroomId, groups),
                    review: groups => api.reviewBoardroomFeatureTwins(boardroomId, groups),
                    keepApart: (a, b) => api.keepBoardroomItemsApart(boardroomId, a, b),
                    search: q =>
                      api
                        .listBoardroomItems(boardroomId, { filter: 'features', q, limit: 20 })
                        .then(page => page.items.map(i => ({ id: i.id, title: i.title }))),
                  }
                : undefined
            }
          />
        ) : data.kind === 'recon_runs' ? (
          <ReconRunsReport
            data={data}
            onOpen={onOpen}
            onLens={tags => onView({ ...view, tags })}
            onTable={onOpenTable}
            loadFindings={loadFindings}
            focusRun={focusRun}
          />
        ) : data.kind === 'who_ranks' ? (
          <WhoRanksReport data={data} />
        ) : data.kind === 'brand_grid' ? (
          <BrandGridReport data={data} onOpen={onOpen} roomName={roomName} />
        ) : data.kind === 'email_capture' ? (
          <EmailCaptureReport data={data} onOpen={onOpen} roomName={roomName} boardroomId={boardroomId} />
        ) : data.kind === 'contacts' ? (
          <ContactsReport data={data} onOpen={onOpen} roomName={roomName} />
        ) : data.kind === 'appeal' ? (
          <AppealReport data={data} onOpen={onOpen} roomName={roomName} boardroomId={boardroomId} />
        ) : data.kind === 'search_market' ? (
          <SearchMarketReport data={data} onOpen={onOpen} roomName={roomName} />
        ) : data.kind === 'related' ? (
          <RelatedReport data={data} onOpen={onOpen} onSet={onSetRelevance ?? (() => {})} busy={sortingKinds} roomName={roomName} />
        ) : data.kind === 'landscape' ? (
          <LandscapeReport data={data} onOpen={onOpen} onPickTag={onPickTag} onDig={onDig} digging={digging} unseen={unseen} onSeen={onSeen} roomName={roomName} />
        ) : data.kind === 'hunches' ? (
          <HunchesReport data={data} onOpen={onOpen} onDig={onDig} digging={digging} unseen={unseen} onSeen={onSeen} roomName={roomName} />
        ) : data.kind === 'competitors' ? (
          <CompetitorsReport data={data} onOpen={onOpen} onDig={onDig} digging={digging} unseen={unseen} onSeen={onSeen} onPickTag={onPickTag} roomName={roomName} />
        ) : data.kind === 'feature_matrix' ? (
          <FeatureMatrixReport data={data} onOpen={onOpen} onDig={onDig} digging={digging} unseen={unseen} onSeen={onSeen} roomName={roomName} columnChange={columnChange} onUndoColumns={onUndoColumns} onSplitType={onSplitType} onUndoSplit={onUndoSplit} splittingType={splittingType} />
        ) : data.kind === 'price_map' ? (
          <PriceMapReport data={data} onOpen={onOpen} onDig={onDig} digging={digging} unseen={unseen} onSeen={onSeen} />
        ) : data.kind === 'audience' ? (
          <AudienceReport data={data} />
        ) : data.kind === 'pain_gap' ? (
          <PainGapReport data={data} onOpen={onOpen} onDig={onDig} digging={digging} unseen={unseen} onSeen={onSeen} />
        ) : data.kind === 'story' ? (
          <StoryReport data={data} onOpen={onOpen} />
        ) : data.kind === 'people' ? (
          <PeopleReport data={data} onOpen={onOpen} />
        ) : data.kind === 'angles' ? (
          <AnglesReport
            data={data}
            onOpen={onOpen}
            canPublish={canPublish}
            onSend={boardroomId ? ids => api.publishBoardroomAngles(boardroomId, { angle_ids: ids }) : undefined}
          />
        ) : data.kind === 'demand' ? (
          <DemandReport data={data} onOpen={onOpen} onDig={onDig} digging={digging} unseen={unseen} onSeen={onSeen} roomName={roomName} />
        ) : (
          <OpportunityBriefReport data={data} onOpen={onOpen} onDig={onDig} digging={digging} unseen={unseen} onSeen={onSeen} roomName={roomName} boardroomId={boardroomId} canPublish={canPublish} />
        )
      ) : (
        <div className="text-xs text-gray-400 py-6">
          {loading ? <UpdatingNote loading digging={digging} /> : 'No report yet.'}
        </div>
      )}
    </div>
  );
}
